// skipcq: JS-W1028
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  collection,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  startAfter,
  where,
} from 'firebase/firestore';
import { db } from '../utils/firebase';
import { CRAWL_SCHEDULES_COLLECTION } from '../utils/firestoreCollections';
import { toDateMaybe } from '../utils/firestoreFieldConversions';

export const CURSOR_ERROR_CODE = 'CRAWL_SCHEDULES_CURSOR_MISSING';

const SCAN_BATCH_SIZE = 50;
const SCAN_MAX_BATCHES = 40;

function mapScheduleDocs(docs) {
  return docs.map((docSnapshot) => {
    const docData = docSnapshot.data();
    return {
      id: docSnapshot.id,
      crawlType: docData.crawlType,
      targetType: docData.targetType,
      targetId: docData.targetId,
      cadence: docData.cadence,
      status: docData.status,
      pausedReason: docData.pausedReason,
      lastErrorMessage: docData.lastErrorMessage,
      retryCount: docData.retryCount,
      nextDueAt: toDateMaybe(docData.nextDueAt),
      updatedAt: toDateMaybe(docData.updatedAt),
      lastSucceededAt: toDateMaybe(docData.lastSucceededAt),
    };
  });
}

function scheduleRowMatchesTargetSubstring(row, needleLower) {
  return String(row.targetId ?? '').toLowerCase().includes(needleLower);
}

/**
 * One page of crawlSchedules (nextDueAt ascending), with Firestore cursor pagination.
 *
 * @param {Object} params
 * @param {number} params.page - Zero-based page index; only adjacent moves are supported by the UI.
 * @param {number} params.rowsPerPage - Page size (max documents fetched is rowsPerPage + 1 to detect a next page).
 * @param {string} params.statusFilter - When non-empty, restrict to this status value.
 * @param {string} params.targetFilter - When non-empty (after trim), client substring filter on targetId (scan mode).
 *
 * When `statusFilter` is set, Firestore requires a composite index on this collection:
 * fields `status` (Ascending) and `nextDueAt` (Ascending). Validate in each Firebase project.
 *
 * Returns `refetchSchedules` — call after successful schedule mutations so scan mode (target substring filter)
 * picks up changes; harmless when using the realtime snapshot query.
 *
 * `scanResultTruncated` is true when target substring scan hit the batch cap and the current page is
 * underfilled (results may be incomplete; pagination does not offer a further page).
 */
function useCrawlSchedules({
  page = 0,
  rowsPerPage = 25,
  statusFilter = '',
  targetFilter = '',
} = {}) {
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [scanResultTruncated, setScanResultTruncated] = useState(false);
  const [refreshNonce, setRefreshNonce] = useState(0);
  /** Last doc snapshot on page `index` (used as startAfter for page index + 1). Snapshot mode only. */
  const [pageBoundaries, setPageBoundaries] = useState([]);
  const scanResumeAfterPageRef = useRef([]);
  const snapshotLoadIdRef = useRef(0);
  const scanLoadIdRef = useRef(0);

  const needsTargetScan = targetFilter.trim().length > 0;

  const refetchSchedules = useCallback(function refetchSchedules() {
    setRefreshNonce((previous) => previous + 1);
  }, []);

  useEffect(() => {
    setPageBoundaries([]);
  }, [rowsPerPage, statusFilter, targetFilter]);

  const startAfterDoc = page === 0 ? null : pageBoundaries[page - 1];

  useEffect(() => {
    if (needsTargetScan) {
      return undefined;
    }

    if (page > 0 && !startAfterDoc) {
      setSchedules([]);
      setHasNextPage(false);
      setLoading(false);
      const cursorError = new Error(
        'Missing pagination cursor for this page. Go back to the first page or change rows per page.',
      );
      cursorError.code = CURSOR_ERROR_CODE;
      setError(cursorError);
      return undefined;
    }

    setSchedules([]);
    setLoading(true);
    setError(null);
    setScanResultTruncated(false);

    const loadId = ++snapshotLoadIdRef.current;
    const collectionRef = collection(db, CRAWL_SCHEDULES_COLLECTION);
    const constraints = [];
    if (statusFilter) {
      constraints.push(where('status', '==', statusFilter));
    }
    constraints.push(orderBy('nextDueAt', 'asc'));
    const windowLimit = limit(rowsPerPage + 1);
    const schedulesPageQuery = startAfterDoc
      ? query(collectionRef, ...constraints, startAfter(startAfterDoc), windowLimit)
      : query(collectionRef, ...constraints, windowLimit);

    const unsub = onSnapshot(
      schedulesPageQuery,
      (snapshot) => {
        if (loadId !== snapshotLoadIdRef.current) {
          return;
        }
        const docs = snapshot.docs;
        const hasNext = docs.length > rowsPerPage;
        const pageDocs = docs.slice(0, rowsPerPage);
        setSchedules(mapScheduleDocs(pageDocs));
        setHasNextPage(hasNext);
        setLoading(false);
        setError(null);
        if (pageDocs.length > 0) {
          const lastVisible = pageDocs[pageDocs.length - 1];
          setPageBoundaries((previous) => {
            const next = [...previous];
            next[page] = lastVisible;
            return next;
          });
        }
      },
      (subscriptionError) => {
        if (loadId !== snapshotLoadIdRef.current) {
          return;
        }
        console.error('useCrawlSchedules', subscriptionError);
        setError(subscriptionError);
        setLoading(false);
      },
    );
    return () => {
      unsub();
      snapshotLoadIdRef.current += 1;
    };
  }, [page, rowsPerPage, startAfterDoc, statusFilter, needsTargetScan, refreshNonce]);

  useEffect(() => {
    if (!needsTargetScan) {
      return undefined;
    }

    const loadId = ++scanLoadIdRef.current;
    setSchedules([]);
    setLoading(true);
    setError(null);
    setScanResultTruncated(false);

    const collectionRef = collection(db, CRAWL_SCHEDULES_COLLECTION);
    const serverConstraints = [];
    if (statusFilter) {
      serverConstraints.push(where('status', '==', statusFilter));
    }

    if (page === 0) {
      scanResumeAfterPageRef.current = [];
    }

    const resumeDoc = page === 0 ? null : scanResumeAfterPageRef.current[page - 1];
    if (page > 0 && resumeDoc === undefined) {
      setSchedules([]);
      setHasNextPage(false);
      setLoading(false);
      const cursorError = new Error(
        'Missing pagination cursor for this page. Go back to the first page or change filters.',
      );
      cursorError.code = CURSOR_ERROR_CODE;
      setError(cursorError);
      return () => {
        scanLoadIdRef.current += 1;
      };
    }

    let readCursor = resumeDoc;
    const matchingDocSnapshots = [];
    let batches = 0;
    let lastBatchSize = 0;
    const needle = targetFilter.trim().toLowerCase();

    async function runScan() {
      try {
        while (
          matchingDocSnapshots.length < rowsPerPage + 1 &&
          batches < SCAN_MAX_BATCHES
        ) {
          batches += 1;
          const batchQuery =
            readCursor == null
              ? query(
                  collectionRef,
                  ...serverConstraints,
                  orderBy('nextDueAt', 'asc'),
                  limit(SCAN_BATCH_SIZE),
                )
              : query(
                  collectionRef,
                  ...serverConstraints,
                  orderBy('nextDueAt', 'asc'),
                  startAfter(readCursor),
                  limit(SCAN_BATCH_SIZE),
                );

          const snap = await getDocs(batchQuery);
          if (loadId !== scanLoadIdRef.current) {
            return;
          }

          lastBatchSize = snap.docs.length;
          if (snap.docs.length === 0) {
            break;
          }

          for (const docSnap of snap.docs) {
            const row = mapScheduleDocs([docSnap])[0];
            if (scheduleRowMatchesTargetSubstring(row, needle)) {
              matchingDocSnapshots.push(docSnap);
            }
          }

          readCursor = snap.docs[snap.docs.length - 1];
        }

        if (loadId !== scanLoadIdRef.current) {
          return;
        }

        const moreInBuffer = matchingDocSnapshots.length > rowsPerPage;
        const hitScanCap = batches >= SCAN_MAX_BATCHES;

        const pageDocSnapshots = moreInBuffer
          ? matchingDocSnapshots.slice(0, rowsPerPage)
          : matchingDocSnapshots;

        const scanStoppedEarlyIncomplete =
          hitScanCap && pageDocSnapshots.length < rowsPerPage;

        let hasNextPageCalc =
          moreInBuffer ||
          (matchingDocSnapshots.length >= rowsPerPage &&
            lastBatchSize === SCAN_BATCH_SIZE) ||
          (hitScanCap && lastBatchSize === SCAN_BATCH_SIZE);

        if (scanStoppedEarlyIncomplete) {
          hasNextPageCalc = false;
          scanResumeAfterPageRef.current.length = page;
          setScanResultTruncated(true);
        } else {
          const resumeAfterLastEmitted =
            pageDocSnapshots.length > 0
              ? pageDocSnapshots[pageDocSnapshots.length - 1]
              : readCursor;
          scanResumeAfterPageRef.current[page] = resumeAfterLastEmitted;
          scanResumeAfterPageRef.current.length = page + 1;
          setScanResultTruncated(false);
        }

        setSchedules(mapScheduleDocs(pageDocSnapshots));
        setHasNextPage(hasNextPageCalc);
        setLoading(false);
        setError(null);
      } catch (scanError) {
        if (loadId !== scanLoadIdRef.current) {
          return;
        }
        console.error('useCrawlSchedules scan', scanError);
        setScanResultTruncated(false);
        setError(scanError);
        setLoading(false);
      }
    }

    runScan().catch(function crawlSchedulesScanUnhandledError() {
      /* runScan sets error state */
    });

    return () => {
      scanLoadIdRef.current += 1;
    };
  }, [page, rowsPerPage, statusFilter, targetFilter, needsTargetScan, refreshNonce]);

  return {
    schedules,
    loading,
    error,
    hasNextPage,
    refetchSchedules,
    scanResultTruncated,
  };
}

export default useCrawlSchedules;
